import React, {memo, useEffect} from 'react'
import {copyResourceTo, copyResponseTo, mutateServices, mutateService} from '../../backend'
import {useSafeState} from '../../hook'
import {Button, Dropdown, Header, Modal, Form, Message, List, Label, Divider, Segment} from 'semantic-ui-react'
import {
  withCopyToServiceData,
  withErrorHandling,
  withHelmet,
  withPropsPresent,
  withServicesData,
  withResourceData,
  withResponseData
} from '../../hoc'
import {getFirstArrayItem, getColorByHttpMethod, asNumber, asInt} from '../../util'
import _, {keys} from 'lodash'

const _CopyTo = (
  {
    handleError,
    services,
    resource,
    response,
    copyResourceTo,
    copyResponseTo,
    copyToService,
    mutateServices,
    mutateService,
    navigate
  }
) => {
  const [state, setState] = useSafeState({
    submitting: false,
    selectedVersionId: undefined,
    selectedEnvironmentId: undefined,
    selectedResourceId: undefined,
    errors: {}
  })

  useEffect(() => {
    setState({
      ...state,
      selectedVersionId: undefined,
      selectedEnvironmentId: undefined,
      selectedResourceId: undefined
    })
  }, [copyToService])

  const getServiceOptions = (services) => {
    return services.map((s) => {
      return {key: s.id, text: s.name, value: s.id}
    })
  }

  const getVersionOptions = (service) => {
    return service.versions.map((v) => {
      return {key: v.id, text: v.name, value: v.id}
    })
  }

  const getEnvironmentOptions = (service, selectedVersionId) => {
    const version = selectedVersionId ? service.versions.find(v => v.id === selectedVersionId) : getFirstArrayItem(service.versions)
    if (!version || !version.environments) {
      return []
    }
    return version.environments.map((e) => {
      return {key: e.id, text: e.name, value: e.id}
    })
  }

  const getResourceOptions = (service, selectedVersionId, selectedEnvironmentId) => {
    const version = selectedVersionId ? service.versions.find(v => v.id === selectedVersionId) : getFirstArrayItem(service.versions)
    if (!version || !version.environments) {
      return []
    }
    const environment = selectedEnvironmentId ? version.environments.find(e => e.id === selectedEnvironmentId) : getFirstArrayItem(version.environments)
    if (!environment || !environment.resources) {
      return []
    }
    return environment.resources.map((r) => {
      return {key: r.id, text: `${r.verb.toUpperCase()} ${r.path}`, value: r.id}
    })
  }

  const onServiceSelected = (e, d) => {
    navigate(`../${d.value}`)
  }

  const onVersionSelected = (e, d) => {
    setState({
      ...state,
      selectedVersionId: d.value,
      selectedEnvironmentId: undefined,
      selectedResourceId: undefined
    })
  }

  const onEnvironmentSelected = (e, d) => {
    setState({
      ...state,
      selectedEnvironmentId: d.value,
      selectedResourceId: undefined
    })
  }

  const onResourceSelected = (e, d) => {
    setState({...state, selectedResourceId: d.value})
  }

  const onSubmit = handleError(async (e) => {
    e.preventDefault()
    if (_.keys(errors).length) {
      setState({...state, ...{errors}})
      return
    }
    setState({...state, submitting: true})
    if (!response && !!resource) {
      try {
        await copyResourceTo(resource.id, copyToService.id, state.selectedVersionId, state.selectedEnvironmentId)
      } catch (e) {
        if (e.status === 409) {
          const errors = state.errors
          errors['resource.present'] = 'Resource already present. Remove or edit the resource in the target service and try again.'
          setState({...state, errors})
          return
        }
        throw e
      }
    }
    if (!!response) {
      try {
        await copyResponseTo(response.id, copyToService.id, state.selectedVersionId, state.selectedEnvironmentId, state.selectedResourceId)
      } catch (e) {
        if (e.status === 409) {
          const errors = state.errors
          errors['response.present'] = 'Response already present. Remove or edit the response in the target service and try again.'
          setState({...state, errors})
          return
        }
        throw e
      }
    }
    mutateServices()
    mutateService()
    onClose()
  })

  const onClose = () => {
    const path = !!response ? '../../../' : '../../'
    navigate(path)
  }

  const serviceOptions = getServiceOptions(services)
  const versionOptions = getVersionOptions(copyToService)
  const environmentOptions = getEnvironmentOptions(copyToService, state.selectedVersionId)
  const resourceOptions = getResourceOptions(copyToService, state.selectedVersionId, state.selectedEnvironmentId)
  const isDisabled = !(!!state.selectedVersionId && !!state.selectedEnvironmentId && (!response || !!state.selectedResourceId))
  const errors = state.errors
  const hasErrors = !!errors['response.present'] || !!errors['resource.present']

  return (
    <Modal open={true} closeOnDimmerClick={true} closeIcon={true} onClose={onClose} data-testid='copy-to-modal'>
      <Header content={response ? 'Copy Response To' : 'Copy Resource To'}/>
      <Modal.Content>
        <div>
          {
            !!resource && !response && (
              <Segment style={{marginBottom: '15px'}} data-testid='copy-to-modal-subject-panel'>
                <Header as='h3' content='Resource'/>
                <div>
                  <List horizontal style={{display: 'inline'}}>
                    <List.Item>
                      <Label color={getColorByHttpMethod(resource.verb)}
                             data-testid='copy-to-resource-verb'
                             style={{
                               marginRight: '10px',
                               width: '75px',
                               textAlign: 'center'
                             }}>{resource.verb.toUpperCase()}</Label>
                    </List.Item>
                    <List.Item style={{maxWidth: '72%'}}  data-testid='copy-to-resource-path'>
                      <b style={{whiteSpace: 'pre-wrap', wordWrap: 'break-word'}}>{resource.path}</b>
                    </List.Item>
                  </List>
                </div>
              </Segment>
            )
          }
          {
            !!response && (
              <Segment style={{marginBottom: '15px'}} data-testid='copy-to-modal-subject-panel'>
                <div style={{fontWeight: 'bold'}}>Response:</div>
                <div style={{marginBottom: '15px'}}>
                  <div style={{fontWeight: 'bold'}}>Response:</div>
                  <div data-testid='copy-to-response-name'>{response.name}</div>
                </div>
                <div style={{marginBottom: '15px'}}>
                  <div style={{fontWeight: 'bold'}}>Status:</div>
                  <div data-testid='copy-to-response-status'>{response.status}</div>
                </div>
                <div style={{marginBottom: '15px'}}>
                  <div style={{fontWeight: 'bold'}}>Content-Type:</div>
                  <div data-testid='copy-to-response-content-type'>{response.contentType}</div>
                </div>
              </Segment>
            )
          }

        </div>
        <Segment>
          <Header as='h3' content='To'/>
          <Form error={hasErrors}>
            <Form.Field error={false}>
              <label>Service</label>
              <Dropdown placeholder='Select service' fluid search selection
                        disabled={serviceOptions.length === 0}
                        options={serviceOptions}
                        value={copyToService.id}
                        data-testid='copy-to-service-dropdown'
                        onChange={onServiceSelected}/>
            </Form.Field>
            <Form.Field>
              <label>Version</label>
              <Dropdown placeholder='Select version' fluid search selection
                        disabled={versionOptions.length === 0}
                        options={versionOptions}
                        value={state.selectedVersionId}
                        data-testid='copy-to-version-dropdown'
                        onChange={onVersionSelected}/>
            </Form.Field>
            <Form.Field>
              <label>Environment</label>
              <Dropdown placeholder='Select environment' fluid search selection
                        disabled={!state.selectedVersionId}
                        options={environmentOptions}
                        value={state.selectedEnvironmentId}
                        data-testid='copy-to-environment-dropdown'
                        onChange={onEnvironmentSelected}/>
            </Form.Field>
            {
              !!response && (
                <Form.Field>
                  <label>Resource</label>
                  <Dropdown placeholder='Select resource' fluid search selection
                            disabled={!state.selectedEnvironmentId}
                            options={resourceOptions}
                            value={state.selectedResourceId}
                            data-testid='copy-to-resource-dropdown'
                            onChange={onResourceSelected}/>
                </Form.Field>
              )
            }
            <Message error data-testid='copy-to-errors'>
              <Message.Content>
                <List bulleted>
                  {
                    keys(errors).map((key) => (
                      <List.Item key={key}>{errors[key]}</List.Item>
                    ))
                  }
                </List>
              </Message.Content>
            </Message>
          </Form>
        </Segment>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} data-testid='copy-to-cancel-button'>Cancel</Button>
        <Button primary disabled={isDisabled} onClick={onSubmit} loading={state.submitting}
                data-testid='copy-to-submit-button'>Copy</Button>
      </Modal.Actions>
    </Modal>
  )
}

let CopyTo = (props) => {
  const {handleError} = props
  props = {
    ...props,
    history: window.history,
    mutateServices,
    mutateService,
    copyResourceTo,
    copyResponseTo
  }
  return (<_CopyTo {...props}/>)
}

CopyTo = withPropsPresent(CopyTo, ['copyToService', 'services'])
CopyTo = withHelmet(CopyTo, 'Copy To')
CopyTo = withCopyToServiceData(CopyTo, 'Loading service...')
CopyTo = withServicesData(CopyTo, 'Loading services...')
CopyTo = withResourceData(CopyTo, 'Loading resource...')
CopyTo = withResponseData(CopyTo, 'Loading response...')
CopyTo = withErrorHandling(CopyTo)
CopyTo = memo(CopyTo)

export {_CopyTo, CopyTo}

export default CopyTo